// ExportGiftVoucherPatient.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'

export interface GiftVoucherRow {
    cnNumber: string
    branchCnNumber: string
    snCode?: string
    patientName: string
    voucherType: string
    voucherName: string
    countWaiting: number
    countReady: number
    countUsed: number
    countExpired: number
}

export interface GiftVoucherReport {
    branchName: string
    typeFilter: string   // e.g. “ทั้งหมด”
    giftFilter: string
    includeSn: boolean
    rows: GiftVoucherRow[]
    t: (key: string) => string
}

export async function exportGiftVoucherPatientToExcel(rpt: GiftVoucherReport) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.TITLE'))
    ws.properties.defaultRowHeight = 20

    // 1) กำหนดคอลัมน์ A–K พร้อม header property
    ws.columns = [
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.NO'), width: 6 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_CN_NUMBER'), width: 14 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.BRANCH_CN_NUMBER'), width: 18 },
        ...(rpt.includeSn ? [{ header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_SN_CODE'), width: 12 }] : []),
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_NAME'), width: 20 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.GIFT_VOUCHER_TYPE'), width: 20 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.GIFT_VOUCHER'), width: 40 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_WAITING'), width: 12 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_READY'), width: 12 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_USED'), width: 12 },
        { header: rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_EXPIRED'), width: 12 }
    ]

    let rowIndex = 1

    // 2) Title row
    ws.mergeCells(rowIndex, 1, rowIndex, ws.columns.length)
    ws.getCell(rowIndex, 1).value = rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.TITLE')
    ws.getCell(rowIndex, 1).font = { size: 16, bold: true }
    ws.getCell(rowIndex, 1).alignment = { horizontal: 'center' }
    rowIndex++

    // 3) Filter row
    ws.mergeCells(rowIndex, 1, rowIndex, ws.columns.length)
    ws.getCell(rowIndex, 1).value = [
        `${rpt.t('REPORT.FILTER.BRANCH')}: ${rpt.branchName}`,
        `${rpt.t('REPORT.FILTER.GIFT_VOUCHER_USED.TYPE')}: ${rpt.typeFilter}`,
        `${rpt.t('REPORT.FILTER.GIFT_VOUCHER_USED.PROMOTION')}: ${rpt.giftFilter}`
    ].join('    ')
    ws.getCell(rowIndex, 1).font = { size: 12 }
    ws.getCell(rowIndex, 1).alignment = { horizontal: 'left' }
    rowIndex += 2

    // 4) Header row (นำ header property มาเป็นแถวจริง)
    const headerRow = ws.addRow(ws.columns.map(c => (c as any).header))
    headerRow.height = 24
    headerRow.font = { bold: true }
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' }
    headerRow.eachCell(cell => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })
    rowIndex++


    if (!rpt.rows || rpt.rows.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, ws.columns.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
        const buf = await wb.xlsx.writeBuffer()
        saveAs(
            new Blob([buf]),
            `${rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.TITLE')}_${moment().format('YYYYMMDD')}.xlsx`
        )
        return
    }

    // 5) Data rows
    rpt.rows.forEach((d, i) => {
        const vals: any[] = [
            i + 1,
            d.cnNumber,
            d.branchCnNumber
        ]
        if (rpt.includeSn) vals.push(d.snCode || '-')
        vals.push(
            d.patientName,
            d.voucherType,
            d.voucherName,
            d.countWaiting,
            d.countReady,
            d.countUsed,
            d.countExpired
        )
        const dataRow = ws.addRow(vals)
        dataRow.alignment = { vertical: 'middle' }
        dataRow.eachCell((cell, col) => {
            // ขอบ cell
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            }

            // คอลัมน์แรก (i+1) จัดกลางแนวนอน

            if (col === 1) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' }
            }
            else if (col > ws.columns.length - 4) {
                cell.alignment = { horizontal: 'center' }
            }
        })
    })

    // 6) Save file
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${rpt.t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.TITLE')}_${moment().format('YYYYMMDD')}.xlsx`
    )
}
